import {Alert, Button} from 'platform/components';
import {Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNil} from 'ramda';

import {VehicleSelectionPreviewResponseBody} from '@dms/api/sales';
import {getApiErrorMessages} from '@dms/api/utils';
import i18n from '@dms/i18n';

import {ApiError, RequiredTestIdProps, suffixTestId, useOnMount} from 'shared/web';

import {VehicleSelectionDraft} from '../../types/VehicleSelectionDraft';
import {VehicleSelectionPreviewResult} from './components/VehicleSelectionPreviewResult';

type PreviewState =
  | {status: 'idle'}
  | {status: 'loading'}
  | {status: 'success'; preview: VehicleSelectionPreviewResponseBody}
  | {status: 'error'; error: ApiError};

interface VehicleSelectionImpactPreviewProps extends RequiredTestIdProps {
  // The owner (promotional photo, watermark) is saved already: its stored rules are previewed
  // once on open.
  isStored: boolean;
  // Read on click, so the preview always reflects the current form state. Resolves to null when
  // the draft fails the form's rules, which then show on the fields instead of a preview of a
  // filter that matches every vehicle.
  getDraft: () => Promise<VehicleSelectionDraft | null>;
  // Fingerprint of the draft inputs. A result belongs to the draft it was computed from, so a
  // changed draft hides it until the next run instead of showing an impact that no longer holds.
  draftKey: string;
  // The owner's preview endpoint; the response has one shape for every owner.
  previewVehicleSelection: (
    draft: VehicleSelectionDraft
  ) => Promise<VehicleSelectionPreviewResponseBody>;
}

// Read-only: the BE computes what saving the draft would change, and assigns nothing. A 403
// (price condition without the selling-price permission, no vehicle to read) and a 400 (an
// inconsistent draft) are shown here as a message, not as a form error.
export function VehicleSelectionImpactPreview(props: VehicleSelectionImpactPreviewProps) {
  const [state, setState] = useState<PreviewState>({status: 'idle'});
  const [previewedKey, setPreviewedKey] = useState<string | null>(null);
  // Each run gets a new number, so a slow response cannot overwrite a newer one.
  const requestRef = useRef(0);

  const runPreview = async () => {
    const draft = await props.getDraft();
    if (isNil(draft)) {
      return;
    }
    requestRef.current += 1;
    const request = requestRef.current;
    setPreviewedKey(props.draftKey);
    setState({status: 'loading'});
    props
      .previewVehicleSelection(draft)
      .then((preview) => {
        if (request === requestRef.current) {
          setState({status: 'success', preview});
        }
      })
      .catch((error: ApiError) => {
        if (request === requestRef.current) {
          setState({status: 'error', error});
        }
      });
  };

  useOnMount(() => {
    if (props.isStored) {
      void runPreview();
    }
  });

  const isCurrent = previewedKey === props.draftKey;
  const hasResult = state.status === 'success' || state.status === 'error';
  const hasOutdatedResult = !isCurrent && hasResult;

  return (
    <VStack spacing={4} data-testid={props['data-testid']}>
      <HStack spacing={4} justify="space-between" align="center">
        <VStack spacing={1}>
          <Heading size={5}>{i18n.t('entity.promoPhotos.preview.labels.title')}</Heading>
          <Text size="xSmall" color="tertiary">
            {i18n.t('entity.promoPhotos.preview.labels.description')}
          </Text>
        </VStack>
        <Button
          variant="outlined"
          leftIcon="action/visibility"
          title={i18n.t('entity.promoPhotos.preview.actions.previewImpact')}
          isLoading={state.status === 'loading'}
          onClick={() => void runPreview()}
          data-testid={suffixTestId('run', props)}
        />
      </HStack>
      <Show when={hasOutdatedResult}>
        <Alert
          variant="info"
          message={i18n.t('entity.promoPhotos.preview.labels.outdated')}
          data-testid={suffixTestId('outdated', props)}
        />
      </Show>
      <Show when={isCurrent && state.status === 'error' ? state.error : null}>
        {(previewError) => (
          <Alert
            variant="error"
            message={getApiErrorMessages(previewError)}
            data-testid={suffixTestId('error', props)}
          />
        )}
      </Show>
      <Show when={isCurrent && state.status === 'success' ? state.preview : null}>
        {(result) => (
          <VehicleSelectionPreviewResult
            preview={result}
            data-testid={suffixTestId('result', props)}
          />
        )}
      </Show>
    </VStack>
  );
}
